import { Icon } from '@platform/components';
import { useEffect, useState } from 'react';
import { ComboBox, Input, Key, Label, ListBox, ListBoxItem, Popover } from 'react-aria-components';

export type NameSearchOption = { id: number; name: string };

type NameSearchDropdownProps = {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  selectedId: number | null | undefined;
  onSelectedIdChange: (id: number | null) => void;
  useSearch: (query: string) => { options: NameSearchOption[]; isFetching: boolean };
  errorText?: string;
  placeholder?: string;
};

const useDebouncedValue = (value: string, delayMs: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
};

export const NameSearchDropdown = (props: NameSearchDropdownProps) => {
  const { label, value, onValueChange, selectedId, onSelectedIdChange, useSearch, errorText, placeholder } =
    props;
  const debouncedQuery = useDebouncedValue(value.trim(), 300);
  const { options, isFetching } = useSearch(debouncedQuery.length >= 2 ? debouncedQuery : '');

  const handleSelectionChange = (key: Key | null) => {
    if (key == null) return;
    const option = options.find((candidate) => String(candidate.id) === String(key));
    if (option) {
      onValueChange(option.name);
      onSelectedIdChange(option.id);
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <ComboBox
        className='flex flex-col gap-2'
        inputValue={value}
        onInputChange={(text) => {
          onValueChange(text);
          if (selectedId != null) onSelectedIdChange(null);
        }}
        selectedKey={selectedId != null ? String(selectedId) : null}
        onSelectionChange={handleSelectionChange}
        allowsCustomValue
        allowsEmptyCollection
        items={options}
      >
        <Label className='text-sm text-gray-500 font-medium'>{label}</Label>
        <div className='relative inline-flex items-center text-[16px] px-3 py-2 bg-white border border-gray-100 rounded-lg shadow-xs has-[:focus]:ring-brand-shadow-xs'>
          <div className='[&>svg>path]:stroke-gray-400 mr-2'>
            <Icon type='search-lg' size='20' />
          </div>
          <Input placeholder={placeholder ?? 'Start typing a name...'} className='outline-none w-full' />
        </div>
        <Popover className='mt-[5px] w-[--trigger-width] max-h-60 overflow-y-auto bg-white shadow-lg border border-gray-100 rounded-lg outline-none'>
          <ListBox
            className='outline-none'
            renderEmptyState={() => (
              <div className='px-3 py-2 text-sm text-gray-400'>
                {isFetching ? 'Searching…'
                : debouncedQuery.length < 2 ? 'Type at least 2 characters to search'
                : 'No matches — this will be saved as free text'}
              </div>
            )}
          >
            {(item: NameSearchOption) => (
              <ListBoxItem
                id={String(item.id)}
                textValue={item.name}
                className='flex items-center rounded-md outline-none cursor-pointer px-2 py-2 mx-1 my-0.5 hover:bg-gray-50 data-[focused]:bg-gray-50'
              >
                {item.name}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </ComboBox>
      {errorText && <div className='text-error-500 text-sm'>{errorText}</div>}
      {selectedId != null && (
        <div className='text-xs text-success-600 flex items-center gap-1'>
          <Icon type='check' size='14' />
          Matched to an existing platform record
        </div>
      )}
    </div>
  );
};
